/**
 * Shared chrome + helpers for the footer breakdown popovers
 * (CostBreakdownPanel, TokenBreakdownPanel).
 *
 * Both popovers read the same per-(model, provider) data from
 * ``GET /conversations/{id}/cost-breakdown`` (the usage ledger — see
 * cowork/metrics/usage.py): one shows the cost side, the other the token side.
 * Keeping the shell, the fetch hook, and the label helpers here keeps the two
 * panels visually and behaviourally identical.
 */

import { useEffect, useState, type ReactNode } from "react";
import { fetchCostBreakdown } from "@/api/cost";
import type { CostBreakdownResponse } from "@/api/types";

const PROVIDER_DISPLAY: Record<string, string> = {
  claude: "Anthropic",
  openrouter: "OpenRouter",
  openai: "OpenAI",
  gemini: "Gemini",
};

const SOURCE_LABEL: Record<string, string> = {
  fetch_and_verify: "fetch",
  perplexity: "research",
  main_agent: "",
};

/** Friendly model name. OpenRouter ids carry the upstream slug after the prefix. */
export function modelLabel(model: string): string {
  if (model === "claude-fable-5-1") return "Claude Fable 5.1";
  if (model === "claude-fable-5") return "Claude Fable 5";
  if (model === "claude-opus-5") return "Claude Opus 5";
  if (model === "claude-opus-4-8") return "Claude Opus 4.8";
  if (model === "claude-opus-4-7") return "Claude Opus 4.7";
  if (model === "claude-sonnet-4-6") return "Claude Sonnet 4.6";
  if (model.startsWith("claude-haiku-4-5")) return "Claude Haiku 4.5";
  if (model.startsWith("openrouter/")) return model.slice("openrouter/".length);
  return model;
}

export function providerDisplayName(provider: string): string {
  return PROVIDER_DISPLAY[provider] ?? provider;
}

/** Non-empty source tags (e.g. "fetch", "research") for the row subtitle. */
export function sourceTagList(sources: string[]): string[] {
  return sources.map((s) => SOURCE_LABEL[s] ?? s).filter(Boolean);
}

export function formatUsd(n: number): string {
  if (n >= 1) return `$${n.toFixed(2)}`;
  if (n >= 0.01) return `$${n.toFixed(3)}`;
  if (n > 0) return `$${n.toFixed(4)}`;
  return "$0";
}

export function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1_000)}K`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

/** Fetch the conversation breakdown once on mount; bails cleanly on unmount. */
export function useBreakdown(conversationId: string) {
  const [data, setData] = useState<CostBreakdownResponse | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchCostBreakdown(conversationId)
      .then((d) => alive && setData(d))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, [conversationId]);
  return { data, error };
}

/** The popover shell: positioning, titled header, close button. */
export function BreakdownPopover({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      /* Mobile (< sm): a fixed, gutter-anchored sheet above the composer — the
         desktop `absolute right-0` anchor slides off-screen left when the
         trigger sits in the composer's left cluster on a narrow viewport. From
         `sm` up it reverts to the original right-aligned 320px popover. */
      className="fixed inset-x-3 bottom-24 max-h-[60vh]
                 sm:absolute sm:inset-x-auto sm:bottom-full sm:right-0 sm:mb-2 sm:w-80 sm:max-h-96
                 overflow-y-auto rounded-lg border border-space-20 bg-surface shadow-lg p-3 z-50 animate-[scaleIn_120ms_ease-out]"
      role="dialog"
      aria-label={title}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-space">{title}</h3>
        <button
          onClick={onClose}
          className="text-space-40 hover:text-space transition-colors"
          aria-label={`Close ${title}`}
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {children}
    </div>
  );
}
